import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPaymentsConfig, createCheckout, migrateSubscribers, getWorkspaceLimits, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FreeUpdates } from "../components/FreeUpdates";
import { toast } from "sonner";
import { CreditCard, ChevronRight, Check, Sparkles, Building2, Loader2, Info } from "lucide-react";

const PRO_FEATURES = [
  "Unlimited migration locations / sites",
  "Full production CUCM → Webex & CUCM 15 runbooks (execute & track)",
  "Migration waves, cutover runsheets & GO/NO-GO gates",
  "Number porting / DID management & evidence sign-off",
  "Estate Scoreboard, Site 360, bulk import & branded PDF exports",
  "Team seats, unlimited AI Migration Guide & firmware insights",
];

const FREE_FEATURES = [
  "Full troubleshooting & diagnostic wizard",
  "5 AI troubleshooting requests per day",
  "CUCM, SIP, CUBE, PRI, Webex, Layer 2/3 & QoS guides",
  "2 demo sites + read-only migration & CUCM 15 preview",
];

export default function Billing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [limits, setLimits] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => {
    getPaymentsConfig().then(setConfig).catch(() => setConfig({ configured: false, price: 19.99, interval: "month" }));
    getWorkspaceLimits().then(setLimits).catch(() => {});
  }, []);

  const plan = limits?.plan || user?.workspace?.plan;
  const isPro = plan === "pro";
  const PLANS_FALLBACK = [
    { id: "monthly", price: 19.99, interval: "month" },
    { id: "yearly", price: 149, interval: "year", saving: "Save 38%" },
  ];
  const plans = config?.plans?.length ? config.plans : PLANS_FALLBACK;
  const selected = plans.find((p) => p.id === cycle) || plans[0];
  const price = Number(selected.price).toFixed(2).replace(/\.00$/, "");
  const per = selected.interval === "year" ? "yr" : "mo";

  const [migrating, setMigrating] = useState(false);
  const runMigrate = async () => {
    setMigrating(true);
    try {
      const r = await migrateSubscribers();
      toast.success(`Moved ${r.moved} subscriber(s) to current prices · ${r.skipped} already current${r.errors.length ? ` · ${r.errors.length} error(s)` : ""}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setMigrating(false);
  };

  const upgrade = async () => {
    setBusy(true);
    try {
      const res = await createCheckout(window.location.origin, cycle);
      if (res?.checkout_url) { window.location.href = res.checkout_url; return; }
      toast.error("Could not start checkout");
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      toast.info(detail || "Billing is not available yet. Please try again shortly.");
    }
    setBusy(false);
  };

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> workspace <ChevronRight className="h-3 w-3" /> billing
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-3xl" data-testid="billing-page">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
          <CreditCard className="h-4 w-4" /> Plans &amp; Billing
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">Upgrade to Pro</h1>
        <p className="text-zinc-400 mt-3">Free is a full troubleshooting workspace. Pro unlocks the complete production migration &amp; estate workflow.</p>

        {/* Current plan */}
        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5 flex items-center gap-4" data-testid="current-plan-card">
          <div className="h-11 w-11 rounded-lg bg-blue-600/15 border border-blue-600/30 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-blue-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white">{user?.workspace?.name || "Your workspace"}</span>
              <span data-testid="billing-plan-badge" className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${isPro ? "bg-emerald-600/15 text-emerald-400 border-emerald-600/30" : "bg-blue-600/15 text-blue-400 border-blue-600/30"}`}>
                {isPro ? "Pro" : "Free"}
              </span>
            </div>
            <div className="text-[12px] text-zinc-500 mt-0.5">
              {isPro
                ? "Active subscription — unlimited locations & full migration workflow."
                : `Free plan · troubleshooting + ${limits?.site_cap ?? 2} demo sites. Upgrade for the full migration workflow.`}
            </div>
          </div>
        </div>

        {/* Free plan summary (only when not Pro) */}
        {!isPro && (
          <div className="mt-5 rounded-lg border border-zinc-800 bg-zinc-900/60 p-5" data-testid="free-plan-card">
            <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-400">Your Free plan includes</div>
            <ul className="mt-3 grid sm:grid-cols-2 gap-2.5">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2 text-[13px] text-zinc-300">
                  <Check className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pro plan card */}
        <div className={`mt-5 rounded-xl border p-6 ${isPro ? "border-emerald-600/40 bg-emerald-600/5" : "border-blue-600/40 bg-gradient-to-b from-blue-600/10 to-transparent"}`} data-testid="pro-plan-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-blue-300">
                <Sparkles className="h-4 w-4" />
                <span className="text-[11px] uppercase tracking-widest font-bold">UC Engineer Pro · Pro</span>
              </div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="font-heading text-5xl font-extrabold text-white">${price}</span>
                <span className="text-zinc-400 text-sm">/{per}</span>
              </div>
              <p className="text-[12px] text-zinc-500 mt-1">No free trial — subscribe &amp; start immediately · cancel anytime · all future updates free</p>
              {!isPro && (
                <div className="mt-4 inline-flex rounded-md border border-zinc-800 bg-zinc-900 p-0.5" data-testid="billing-cycle-switch">
                  {plans.map((p) => (
                    <button key={p.id} onClick={() => setCycle(p.id)} data-testid={`cycle-${p.id}`}
                      className={`text-[12px] font-semibold px-3 py-1.5 rounded transition-colors ${cycle === p.id ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-white"}`}>
                      {p.id === "yearly" ? "Annual" : "Monthly"}{p.saving ? ` · ${p.saving}` : ""}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <ul className="mt-5 grid sm:grid-cols-2 gap-2.5">
            {PRO_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-2 text-[13px] text-zinc-200">
                <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>

          <div className="mt-6">
            {isPro ? (
              <div className="flex items-center gap-2 text-[13px] text-emerald-400 font-semibold">
                <Check className="h-4 w-4" /> You're on Pro — thank you!
              </div>
            ) : (
              <>
                <button
                  onClick={upgrade}
                  disabled={busy || (config && !config.configured)}
                  data-testid="upgrade-btn"
                  className="w-full sm:w-auto flex items-center justify-center gap-2 text-sm font-semibold px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                  {config && !config.configured ? "Billing coming soon" : `Upgrade — $${price}/${per}`}
                </button>
                {config && !config.configured && (
                  <div className="mt-3 flex items-start gap-2 text-[12px] text-zinc-500" data-testid="billing-coming-soon">
                    <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    Card payments are being provisioned. Your Free plan stays active in the meantime — we'll enable checkout here shortly.
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="mt-5"><FreeUpdates /></div>

        {user?.is_admin && (
          <div className="mt-5 rounded-lg border border-zinc-800 bg-zinc-900 p-4 flex items-center gap-3 flex-wrap" data-testid="admin-migrate-card">
            <div className="flex-1 min-w-[200px] text-[12px] text-zinc-400">
              <span className="text-white font-semibold">Admin:</span> move every existing subscriber onto the current Pro prices (takes effect at their next renewal, no proration).
            </div>
            <button onClick={runMigrate} disabled={migrating} data-testid="admin-migrate-btn"
              className="flex items-center gap-2 text-[12px] font-semibold px-4 py-2 rounded-md bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-white transition-colors">
              {migrating && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Move subscribers to new prices
            </button>
          </div>
        )}

        <button onClick={() => navigate("/team")} className="mt-6 text-[12px] text-blue-400 hover:text-blue-300 font-medium">
          Manage your team & seats →
        </button>
      </div>
    </div>
  );
}
